import React, { useState } from "react";
import cn from "classnames";
import useDarkMode from "use-dark-mode";
import Swal from "sweetalert2";
import { useNavigate } from 'react-router-dom';

import styles from "./Released.module.sass";
import Card from "../../components/Card";
import Form from "../../components/Form";
import { Tooltip } from "antd";
import AccessControl from "../../HOC/AccessControl";
import CreateMultipleTotes from "./Modals/CreateMultipleTotes";
import DeleteTotes from "./Modals/DeleteTotes";
import Icon from "../../components/Icon";
import TotesTable from "./TotesTable";
import EmptyTotes from "./Modals/EmptyTotes";
import DeleteSingleTote from "./Modals/DeleteSingleTote";
import EmptySingleTote from "./Modals/EmptySingleTote";
import PrintToteCodes from "./Modals/PrintToteCodes";
import { BARCODE_LOCAL_STORAGE_KEY } from '../../constants';

const Totes = () => {
  const [searchVisible, setSearchVisible] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [search, setSearch] = useState("");
  const [activeButton, setActiveButton] = useState(null);
  const [iconVisible, setIconVisible] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [checkedItems, setCheckedItems] = useState({});
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(100);
  const [customFieldTrigger, setCustomFieldTrigger] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const savedPageSize = localStorage.getItem("itemsPerPage");
    return savedPageSize ? parseInt(savedPageSize, 10) : 10;
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isEmptyModalVisible, setIsEmptyModalVisible] = useState(false);
  const [isPrintModalVisible, setIsPrintModalVisible] = useState(false);
  const [toteMarkedToDelete, setToteMarkedToDelete] = useState(null);
  const [toteMarkedToEmpty, setToteMarkedToEmpty] = useState(null);
  const [newCreatedTotes, setNewCreatedTotes] = useState([]);
  const [actionType, setActionType] = useState("deleteTote");
  const darkMode = useDarkMode(false);
  const navigate = useNavigate();

  const downloadCSV = (barcodes) => {
    if (barcodes.length === 0) return;

    // 1) Create the CSV content
    let csvContent = "";

    // 2) Add each barcode on a new line
    barcodes.forEach((code) => {
        csvContent += code + "\n";
    });

    // 3) Create a Blob and use a temporary link to trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "tote_codes.csv";
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

  const handleCreateMultipleToteModalClose = () => {
    setIsModalVisible(false);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalVisible(false);
  }

  const handleEmptyModalClose = () => {
    setIsEmptyModalVisible(false);
  }

  const handlePrintModalClose = () => {
    setIsPrintModalVisible(false);
    setRefetchTrigger((prev) => prev + 1);
  }

  const handleSingleDeleteToteModalClose = () => {
    setToteMarkedToDelete(null);
  }

  const handleSingleEmptyToteModalClose = () => {
    setToteMarkedToEmpty(null);
  }

  const handleCreateMultipleToteDeleteConfirm = async () => {
    setIsModalVisible(false);
  };

  const handleItemsPerPageChange = (newPageSize) => {
    setItemsPerPage(newPageSize);
    localStorage.setItem("itemsPerPage", newPageSize.toString());
  };

  const handleCheckboxChange = (event, newCheckedItems = null, id = null) => {
    if (newCheckedItems) {
      setCheckedItems(newCheckedItems);
    } else if (id !== null) {
      setCheckedItems((prev) => {
        const updated = { ...prev, [id]: !prev[id] };
        return updated;
      });
    }
  };

  const handleSearchIconClick = () => {
    setSearchVisible(true);
    setIconVisible(false);
    setActiveButton("search");
  };

  const handleClickCreateMultipleTote = () => {
    setIsModalVisible(true);
  };
  const handleCreateMultipleToteSuccess = async () => {
    setIsModalVisible(false);
    setIsPrintModalVisible(true);
  };

  const handleDeleteSuccess = async () => {
    setIsDeleteModalVisible(false);
    setRefetchTrigger((prev) => prev + 1);
  }

  const handleEmptySuccess = async () => {
    setIsEmptyModalVisible(false);
    setRefetchTrigger((prev) => prev + 1);
  }

  const handleSingleToteDeleteSuccess = async () => {
    setToteMarkedToDelete(null);
    setRefetchTrigger((prev) => prev + 1);
  }

  const handleSingleEmptyToteSuccess = async () => {
    setToteMarkedToEmpty(null);
    setRefetchTrigger((prev) => prev + 1);
  }

  const handlePrintToteCodesSuccess = async () => {
    setIsPrintModalVisible(false);
    setRefetchTrigger((prev) => prev + 1);
  }

  const handleClickTab = (tab) => {
    setActiveTab(tab);
    setCheckedItems({});
  };

  const handleCancelButtonClick = () => {
    setSearchVisible(false);
    setIconVisible(true);
    setSearch("");
    setActiveButton(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
  };


  const handleDeleteClick = () => {
    const selectedItems = Object.keys(checkedItems).filter(
      (id) => checkedItems[id]
    );
    if (selectedItems.length === 0) {
          Swal.fire({
            icon: "warning",
            title: "No Totes Selected",
            text: "Please select one or multiple totes.",
          });
        } else {
          setIsDeleteModalVisible(true);
        }
  };

  const handleEmptyClick = () => {
    const selectedItems = Object.keys(checkedItems).filter(
      (id) => checkedItems[id]
    );
    if (selectedItems.length === 0) {
          Swal.fire({
            icon: "warning",
            title: "No Totes Selected",
            text: "Please select one or multiple totes.",
          });
        } else {
          setIsEmptyModalVisible(true);
        }
  }

  const handleExportIconClick = () => {
    setActiveButton("export");

    const selectedItems = Object.keys(checkedItems).filter(
      (id) => checkedItems[id]
    );

    if (selectedItems.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Totes Selected",
        text: "Please select one or multiple totes.",
      });
      return;
    }

    const selectedTotes = selectedItems.map((id) => id);
    downloadCSV(selectedTotes);
  };

  const handlePrintIconClick = () => {
    setActiveButton("print");
    const barcodes = Object.keys(checkedItems).filter(
      (id) => checkedItems[id]
    );
    localStorage.setItem(BARCODE_LOCAL_STORAGE_KEY, JSON.stringify(barcodes));
    navigate('/barcodeprint');
  };
  const tabs = [
    { key: "all", label: "All" },
    { key: "empty", label: "Empty" },
    { key: "in_use", label: "In Use" },
  ];
  const iconButtons = [
    {
      name: "search",
      onClick: handleSearchIconClick,
      isActive: activeButton === "search",
      visible: iconVisible,
    },
    {
      name: "export",
      onClick: handleExportIconClick,
      isActive: activeButton === "export",
      visible: true,
    },
    {
      name: "print",
      onClick: handlePrintIconClick,
      isActive: activeButton === "print",
      visible: true,
    },
  ];
  return (
    <>
      <Card className={styles.Card}>
        <div className="flex justify-between items-center  mb-12">
          <div className={cn("title-blue", styles.subtitle)}>All Totes</div>
          <div className="flex items-center space-x-2">
            <AccessControl moduleCode="9a7df7" requiredPrivilege="82c962">
              <button
                className={cn(
                  "button-clear-stroke button-clear-stroke-red button-small",
                  styles.button
                )}
                onClick={handleDeleteClick}
              >
                Delete
              </button>
            </AccessControl>
            <AccessControl moduleCode="9a7df7" requiredPrivilege="3dea03">
              <button className={cn("button-stroke button-small")} onClick={handleEmptyClick}>
                Empty Tote
              </button>
            </AccessControl>
            <AccessControl moduleCode="9a7df7" requiredPrivilege="496b3e">
              <button
                className={cn("button", styles.button)}
                onClick={handleClickCreateMultipleTote}
              >
                <span>Create New Totes</span>
              </button>
            </AccessControl>
          </div>
        </div>

        <CreateMultipleTotes
          handleDeleteConfirm={handleCreateMultipleToteDeleteConfirm}
          handleModalClose={handleCreateMultipleToteModalClose}
          handleSuccess={handleCreateMultipleToteSuccess}
          isModalVisible={isModalVisible}
          setNewCreatedTotes={setNewCreatedTotes}
        />

        <DeleteTotes 
          checkedItems={checkedItems}
          handleModalClose={handleDeleteModalClose}
          isModalVisible={isDeleteModalVisible}
          handleSuccess={handleDeleteSuccess}
        />

        <EmptyTotes
          checkedItems={checkedItems}
          handleModalClose={handleEmptyModalClose}
          isModalVisible={isEmptyModalVisible}
          handleSuccess={handleEmptySuccess}
        />

        <DeleteSingleTote 
          handleModalClose={handleSingleDeleteToteModalClose}
          handleSuccess={handleSingleToteDeleteSuccess}
          isModalVisible={toteMarkedToDelete !== null}
          toteMarkedToDelete={toteMarkedToDelete}
          actionType={actionType}          
        />

        <EmptySingleTote
          handleModalClose={handleSingleEmptyToteModalClose}
          handleSuccess={handleSingleEmptyToteSuccess}
          isModalVisible={toteMarkedToEmpty !== null}
          toteMarkedToEmpty={toteMarkedToEmpty}
        />

        <PrintToteCodes
           handleModalClose={handlePrintModalClose}
           handleSuccess={handlePrintToteCodesSuccess}
           isModalVisible={isPrintModalVisible}
           newCreatedTotesCode={newCreatedTotes}
        />

        <div className="flex gap-4 sm:flex-col sm:gap-2 md:flex-col md:gap-2  w-full">
          {searchVisible && (
            <div className="w-[100%] flex">
              <Form
                className={cn(
                  styles.form,
                  "w-[100%]",
                  " sm:w-full",
                  "md:w-[100%]",
                  "lg:w-[100%]",
                  "xl:w-[100%]"
                )}
                value={search}
                setValue={setSearch}
                onSubmit={handleSubmit}
                placeholder="Search By Tote Code"
                type="text"
                name="search"
                icon="search"
                autoFocus={true}
              />
              <button
                className={cn(
                  "button-stroke button-small",
                  styles.button,
                  "ml-[2%] mr-[1%]",
                  "whitespace-nowrap px-2.5 py-2 text-[13px]"
                )}
                onClick={handleCancelButtonClick}
              >
                Cancel
              </button>
            </div>
          )}

          {iconVisible && (
            <div className="flex items-center gap-4 sm:flex-col sm:gap-2 md:flex-col md:gap-2 w-full">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleClickTab(tab.key)}
                  className={`px-4 py-2 rounded-xl transition-colors ${
                    activeTab === tab.key
                      ? darkMode.value
                        ? "bg-[#272B30]"
                        : "bg-gray-300"
                      : ""
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-[15px]">
            {iconButtons.map(
              (icon) =>
                icon.visible && (
                  <Tooltip
                    key={icon.name}
                    title={
                      icon.name.charAt(0).toUpperCase() + icon.name.slice(1)
                    }
                  >
                    <div
                      className={cn(
                        "flex border-2 border-[#F4F4F4] p-[5px_9px] h-[38px] rounded-[8px]",
                        styles.filtericon
                      )}
                    >
                      <button
                        className={cn(styles.link, {
                          [styles.active]: icon.isActive,
                        })}
                        onClick={icon.onClick}
                      >
                        <Icon name={icon.name} size="24" />
                      </button>
                    </div>
                  </Tooltip>
                )
            )}
          </div>
        </div>
      </Card>
      <div className={cn(styles.wrapper)}>
        <TotesTable
          handleCheckboxChange={handleCheckboxChange}
          checkedItems={checkedItems}
          page={page}
          setPage={setPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={handleItemsPerPageChange}
          setTotalItems={setTotalItems}
          customFieldTrigger={customFieldTrigger}
          activeTab={activeTab}
          refetchTrigger={refetchTrigger}
          setToteMarkedToDelete={setToteMarkedToDelete}
          setToteMarkedToEmpty={setToteMarkedToEmpty}
          search={search}
        />
      </div>
    </>
  );
};

export default Totes;
